import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ValidationErrorCode } from '@vendasta/automata';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { getFieldViolationsFromError } from '@vendasta/shared';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, shareReplay, take } from 'rxjs/operators';
import { initialTemplateCreationQueryParam } from '../../common/constants';
import { WorkflowWarning, WorkflowWarningCalculatorService } from './workflow-warning-calculator.service';
import { CurrentAutomationService } from '@galaxy/automata/shared';

interface Error {
  code: number;
  message?: string;
  details?: ValidationErrorData;
}

export interface ValidationErrorData {
  stepId: string;
  taskDefinitionId: string;
  errorMessage: string;
  isNonDismissable?: boolean;
}

const InvalidArgument = 3;

@Injectable({ providedIn: 'root' })
export class AutomationErrorHandlerService {
  warningInsteadOfError$: Observable<boolean> = this.route.queryParamMap.pipe(
    map((params) => !!params.get(initialTemplateCreationQueryParam)),
  );

  private validationErrorMap$$: BehaviorSubject<Map<string, ValidationErrorData>> = new BehaviorSubject<
    Map<string, ValidationErrorData>
  >(new Map());
  validationErrorMap$: Observable<Map<string, ValidationErrorData>> = this.validationErrorMap$$
    .asObservable()
    .pipe(shareReplay(1));

  hasBlockingError$: Observable<boolean>;
  hasBlockingWarning$: Observable<boolean>;

  workflowWarnings$: Observable<WorkflowWarning[]> = this.workflowWarningService.workflowWarnings$;

  clearValidationErrorMap(): void {
    this.validationErrorMap$$.next(new Map());
  }

  constructor(
    private readonly snackbarService: SnackbarService,
    private readonly translateService: TranslateService,
    private readonly currentAutomationService: CurrentAutomationService,
    private readonly route: ActivatedRoute,
    private readonly workflowWarningService: WorkflowWarningCalculatorService,
  ) {
    const validationError$ = this.validationErrorMap$.pipe(
      map((errMap) => {
        let hasNonDismissableError = false;
        for (const validationError of errMap.values()) {
          if (validationError.isNonDismissable) {
            hasNonDismissableError = true;
          }
        }

        return hasNonDismissableError;
      }),
    );
    this.hasBlockingError$ = combineLatest([validationError$, this.warningInsteadOfError$]).pipe(
      map(([hasValidationError, isWarning]) => {
        return hasValidationError && !isWarning;
      }),
    );
    this.hasBlockingWarning$ = combineLatest([validationError$, this.warningInsteadOfError$]).pipe(
      map(([hasValidationError, isWarning]) => {
        return hasValidationError && !!isWarning;
      }),
    );
  }

  deleteValidationError(stepId: string): void {
    const errorMap = this.validationErrorMap$$.getValue();
    errorMap.delete(stepId);
    this.validationErrorMap$$.next(errorMap);
  }

  setValidationError(details: ValidationErrorData): void {
    const errorMap = this.validationErrorMap$$.getValue();
    errorMap.set(details.stepId, details);
    this.validationErrorMap$$.next(errorMap);
  }

  setValidationErrorWithExpiration(details: ValidationErrorData, errorMessage: string): number {
    this.setValidationError(details);
    return window.setTimeout(() => {
      this.deleteValidationError(details?.stepId);
    }, this.snackbarService.durationForText(errorMessage));
  }

  private getStepMissingDataTranslationKey(isTrigger = false, isTriggerFilter = false): string {
    if (isTrigger) {
      if (isTriggerFilter) {
        return this.translateService.instant('AUTOMATIONS.EDITOR.ERRORS.TRIGGER_MISSING_DATA_FOR_FILTER');
      } else {
        return this.translateService.instant('AUTOMATIONS.EDITOR.ERRORS.TRIGGER_MISSING_DATA_FOR_STEP');
      }
    } else {
      return this.translateService.instant('AUTOMATIONS.EDITOR.ERRORS.STEP_MISSING_DATA');
    }
  }

  private displayValidationError(httpError: HttpErrorResponse, isTrigger: boolean): void {
    const fieldViolations = getFieldViolationsFromError(httpError);

    let validationError: Error;
    try {
      validationError = JSON.parse(httpError.error?.message);
    } catch (_) {
      validationError = { code: ValidationErrorCode.GenericError, message: httpError.error?.message };
    }

    switch (validationError?.code) {
      case ValidationErrorCode.StepMissingDataError:
        if (fieldViolations?.find((fv) => fv.field === 'Automation.Goal')) {
          this.snackbarService.openErrorSnack(
            'AUTOMATIONS.EDITOR.ERRORS.CANT_CHANGE_TRIGGER_BECAUSE_OF_GOAL_REFERENCE',
          );
          return;
        }

        this.currentAutomationService.automation$
          .pipe(
            take(1),
            map((automation) => {
              return !!automation?.triggerFilters?.find((f) => f.id === validationError?.details?.stepId);
            }),
          )
          .subscribe((isTriggerFilterError) => {
            const errorMessage = this.getStepMissingDataTranslationKey(isTrigger, isTriggerFilterError);
            const timeout = this.setValidationErrorWithExpiration(validationError?.details, errorMessage);

            this.snackbarService.errorSnack(errorMessage, '', 0, () => {
              window.clearTimeout(timeout);
              this.deleteValidationError(validationError.details?.stepId);
            });
          });
        return;

      case ValidationErrorCode.OrphanedStepsError:
        this.snackbarService.openErrorSnack('AUTOMATIONS.EDITOR.ERRORS.CANT_ADD_TERMINATING_NODE_WITHIN_AUTOMATION');
        return;

      case ValidationErrorCode.PreviousStepReferenceError:
        this.snackbarService.openErrorSnack('AUTOMATIONS.EDITOR.ERRORS.PREVIOUS_STEP_REFERENCE_ERROR');
        return;

      case ValidationErrorCode.GenericError:
        if (fieldViolations?.find((fv) => fv.field === 'Automation.WorkflowStep.Choices')) {
          this.snackbarService.openErrorSnack('AUTOMATIONS.EDITOR.ERRORS.CANNOT_ALTER_PASSED_PARAMETERS');
          return;
        }
        if (fieldViolations?.find((fv) => fv.field === 'Automation.WorkflowStep.Jumps')) {
          this.snackbarService.openErrorSnack('AUTOMATIONS.EDITOR.ERRORS.TOO_MANY_JUMPS');
          return;
        }
        this.snackbarService.openErrorSnack('COMMON.ERROR_TRY_AGAIN');
        return;

      default:
        this.snackbarService.openErrorSnack('COMMON.ERROR_TRY_AGAIN');
        return;
    }
  }

  displayError(err: HttpErrorResponse, isTrigger = false): void {
    if (!err.error) {
      this.snackbarService.openErrorSnack('COMMON.ERROR_TRY_AGAIN');
      return;
    }

    switch (err?.error?.code) {
      case InvalidArgument:
        this.displayValidationError(err, isTrigger);
        return;
      default:
        this.snackbarService.openErrorSnack(this.translateService.instant('COMMON.ERROR_TRY_AGAIN'));
        return;
    }
  }

  hasError(stepId: string): Observable<boolean> {
    return combineLatest([this.validationErrorMap$, this.warningInsteadOfError$]).pipe(
      map(([errMap, isWarning]) => !isWarning && !!errMap.get(stepId)),
    );
  }

  hasWarning(stepId: string): Observable<boolean> {
    return combineLatest([this.validationErrorMap$, this.warningInsteadOfError$, this.workflowWarnings$]).pipe(
      map(([errMap, isWarning, warnings]) => {
        const warning = (warnings || []).find((w) => w.highlightStepId === stepId);
        return (!!isWarning && !!errMap.get(stepId)) || !!warning;
      }),
    );
  }

  getError(stepId: string): Observable<ValidationErrorData> {
    return this.validationErrorMap$.pipe(map((errMap) => errMap.get(stepId)));
  }
}
